import { lazy, Suspense, useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { PageLoadingSpinner } from './LoadingSpinner';

// Lazy load main pages for better code splitting
const Contact = lazy(() => import('../pages/Contact'));
const Landing = lazy(() => import('../pages/Landing'));
const Portfolio = lazy(() => import('../pages/Portfolio'));
const Projects = lazy(() => import('../pages/Projects'));
const Wiggleworm = lazy(() => import('../pages/Wiggleworm'));

// Lazy load admin components for better code splitting
const ImprovedContentSectionsManager = lazy(() =>
	import('../admin/content/improved-sections-manager').then((module) => ({
		default: module.ImprovedContentSectionsManager,
	}))
);
const AdminDashboard = lazy(() =>
	import('../admin/dashboard').then((module) => ({
		default: module.AdminDashboard,
	}))
);
const FieldManager = lazy(() =>
	import('../admin/fields/field-manager').then((module) => ({
		default: module.FieldManager,
	}))
);
const ImprovedFieldManager = lazy(() =>
	import('../admin/fields/improved-field-manager').then((module) => ({
		default: module.ImprovedFieldManager,
	}))
);
const EnhancedMediaManager = lazy(() =>
	import('../admin/media/enhanced-manager').then((module) => ({
		default: module.EnhancedMediaManager,
	}))
);
const ProjectCreate = lazy(() =>
	import('../admin/projects/create').then((module) => ({
		default: module.ProjectCreate,
	}))
);
const ProjectEdit = lazy(() =>
	import('../admin/projects/edit').then((module) => ({
		default: module.ProjectEdit,
	}))
);
const EnhancedProjectsManager = lazy(() =>
	import('../admin/projects/enhanced-manager').then((module) => ({
		default: module.EnhancedProjectsManager,
	}))
);
// Debug component removed

const routeComponents: Record<string, React.ComponentType> = {
	'/': Landing,
	'/portfolio': Portfolio,
	'/projects': Projects,

	'/wiggleworm': Wiggleworm,
	'/contact': Contact,
};

const adminRouteComponents: Record<string, React.ComponentType> = {
	'/admin': AdminDashboard,
	'/admin/projects': EnhancedProjectsManager,
	'/admin/projects/create': ProjectCreate,
	'/admin/media': EnhancedMediaManager,
	'/admin/content': ImprovedContentSectionsManager,
	'/admin/fields': ImprovedFieldManager,
	'/admin/fields/custom': FieldManager,

	// Debug route removed
};

// Dynamic route handler for admin routes
const getAdminComponent = (pathname: string): React.ComponentType | null => {
	// Handle exact matches first
	if (adminRouteComponents[pathname]) {
		return adminRouteComponents[pathname];
	}

	// Handle dynamic routes
	if (pathname.startsWith('/admin/projects/edit/')) {
		return ProjectEdit;
	}

	return null;
};

export const PageTransition = () => {
	const location = useLocation();
	const [displayLocation, setDisplayLocation] = useState(location.pathname);
	const [, setIsTransitioning] = useState(false);
	const [transitionClass, setTransitionClass] = useState('page-fade-in');
	const transitionTimeoutRef = useRef<NodeJS.Timeout>();

	useEffect(() => {
		if (location.pathname !== displayLocation) {
			// Clear any existing timeout
			if (transitionTimeoutRef.current) {
				clearTimeout(transitionTimeoutRef.current);
			}

			// Start transition
			setIsTransitioning(true);
			setTransitionClass('page-fade-out');

			// After fade out, switch content and fade in
			transitionTimeoutRef.current = setTimeout(() => {
				setDisplayLocation(location.pathname);
				setTransitionClass('page-fade-in');

				// Complete transition
				transitionTimeoutRef.current = setTimeout(() => {
					setIsTransitioning(false);
				}, 150);
			}, 120);
		}

		return () => {
			if (transitionTimeoutRef.current) {
				clearTimeout(transitionTimeoutRef.current);
			}
		};
	}, [location.pathname, displayLocation]);

	// Get the component for the current route
	const Component =
		routeComponents[displayLocation] || (() => <div>Page Not Found</div>);

	return (
		<div className={`transition-wrapper ${transitionClass}`}>
			<Suspense fallback={<PageLoadingSpinner />}>
				<Component />
			</Suspense>
		</div>
	);
};

export const AdminPageTransition = () => {
	const location = useLocation();
	const [displayLocation, setDisplayLocation] = useState(() => {
		// Clean up trailing slash
		return location.pathname === '/admin/' ? '/admin' : location.pathname;
	});
	const [, setIsTransitioning] = useState(false);
	const [transitionClass, setTransitionClass] = useState('page-fade-in');
	const transitionTimeoutRef = useRef<NodeJS.Timeout>();

	useEffect(() => {
		const targetPath =
			location.pathname === '/admin/' ? '/admin' : location.pathname;

		if (targetPath !== displayLocation) {
			// Clear any existing timeout
			if (transitionTimeoutRef.current) {
				clearTimeout(transitionTimeoutRef.current);
			}

			// Start transition
			setIsTransitioning(true);
			setTransitionClass('page-fade-out');

			// After fade out, switch content and fade in
			transitionTimeoutRef.current = setTimeout(() => {
				setDisplayLocation(targetPath);
				setTransitionClass('page-fade-in');

				// Complete transition
				transitionTimeoutRef.current = setTimeout(() => {
					setIsTransitioning(false);
				}, 150);
			}, 120);
		}

		return () => {
			if (transitionTimeoutRef.current) {
				clearTimeout(transitionTimeoutRef.current);
			}
		};
	}, [location.pathname, displayLocation]);

	// Handle dynamic routes like /admin/projects/edit/:id
	let Component = getAdminComponent(displayLocation);

	if (!Component) {
		// More helpful error message with the actual path
		Component = () => (
			<div className='p-8 text-center'>
				<h2 className='text-xl font-bold text-red-600 mb-4'>
					Admin Page Not Found
				</h2>
				<p className='text-gray-600 mb-4'>
					Could not find route:{' '}
					<code className='bg-gray-100 px-2 py-1 rounded'>
						{displayLocation}
					</code>
				</p>
				<p className='text-sm text-gray-500'>Available routes:</p>
				<ul className='text-sm text-gray-500 mt-2'>
					<li>/admin (Dashboard)</li>
					<li>/admin/projects</li>
					<li>/admin/content</li>
					<li>/admin/fields</li>
					<li>/admin/fields/custom</li>
					<li>/admin/media</li>
					{/* Debug route removed */}
				</ul>
			</div>
		);
	}

	return (
		<div className={`transition-wrapper ${transitionClass}`}>
			<Suspense fallback={<PageLoadingSpinner message='Loading admin...' />}>
				<Component />
			</Suspense>
		</div>
	);
};
